import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, EyeOff, Lightbulb } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { apiClient } from '../../lib/api-client';

import { type ClientSuggestion, apiErrorMessage, formatDate } from './suggestion-meta';
import { GainPill, SuggestionIconChip } from './SuggestionCard';

/** Fiche d'une suggestion côté client : présentation, puis « Ça m'intéresse » / « Pas pour moi ». */
export function SuggestionDetailModal({
  suggestion,
  onClose,
}: {
  suggestion: ClientSuggestion | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setMessage('');
    setError(null);
  }, [suggestion?.id]);

  async function refresh() {
    await qc.invalidateQueries({ queryKey: ['suggestions'] });
    await qc.invalidateQueries({ queryKey: ['automation-requests'] });
  }

  const respond = useMutation({
    mutationFn: (action: 'interest' | 'dismiss' | 'restore') => {
      const url = `/suggestions/${suggestion?.id}/${action === 'restore' ? 'dismiss' : action}`;
      if (action === 'interest') {
        return apiClient.post(url, message.trim() ? { message: message.trim() } : {});
      }
      return action === 'dismiss' ? apiClient.post(url) : apiClient.delete(url);
    },
    onMutate: () => setError(null),
    onSuccess: async (_data, action) => {
      await refresh();
      if (action === 'dismiss') onClose();
    },
    onError: (err) =>
      setError(apiErrorMessage(err, 'L’action n’a pas abouti. Réessayez dans un instant.')),
  });

  const response = suggestion?.response ?? null;

  return (
    <Modal
      open={!!suggestion}
      onClose={onClose}
      title={suggestion?.title ?? ''}
      size="lg"
      footer={
        <Button variant="secondary" type="button" onClick={onClose}>
          Fermer
        </Button>
      }
    >
      {suggestion && (
        <div className="space-y-5">
          <div className="flex items-start gap-4">
            <SuggestionIconChip suggestion={suggestion} size="lg" />
            <div className="min-w-0 space-y-2">
              <p className="text-base font-medium text-slate-900">{suggestion.tagline}</p>
              <div className="flex flex-wrap items-center gap-2">
                {suggestion.category && (
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                    {suggestion.category}
                  </span>
                )}
                <GainPill hours={suggestion.hoursSavedPerMonth} />
              </div>
            </div>
          </div>

          <p className="whitespace-pre-line text-sm leading-relaxed text-slate-700">
            {suggestion.description}
          </p>

          {suggestion.example && (
            <div className="rounded-lg bg-slate-50 p-4">
              <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-slate-500">
                <Lightbulb className="h-3.5 w-3.5" aria-hidden="true" />
                Exemple concret
              </div>
              <p className="whitespace-pre-line text-sm text-slate-800">{suggestion.example}</p>
            </div>
          )}

          <div className="border-t border-slate-100 pt-4">
            {response?.type === 'INTERESTED' ? (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-emerald-50 px-4 py-3">
                <div className="flex items-center gap-2 text-sm text-emerald-800">
                  <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
                  <span>
                    Demande envoyée le {formatDate(response.createdAt)}
                    {response.byName ? ` par ${response.byName}` : ''}. Adweb-Systèmes revient vers
                    vous.
                  </span>
                </div>
                {response.requestId && (
                  <Link
                    to={`/demandes?id=${response.requestId}`}
                    className="text-sm font-medium text-brand hover:underline"
                  >
                    Suivre la demande
                  </Link>
                )}
              </div>
            ) : response?.type === 'DISMISSED' ? (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-slate-50 px-4 py-3">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <EyeOff className="h-4 w-4 shrink-0" aria-hidden="true" />
                  Suggestion masquée pour votre organisation.
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => respond.mutate('restore')}
                  disabled={respond.isPending}
                >
                  Réafficher
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <label htmlFor="sugg-message" className="block text-xs font-medium text-slate-700">
                  Une précision pour Adweb-Systèmes ? (facultatif)
                </label>
                <textarea
                  id="sugg-message"
                  rows={3}
                  maxLength={2000}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Vos outils, votre volume, vos questions…"
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                />
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => respond.mutate('dismiss')}
                    disabled={respond.isPending}
                  >
                    Pas pour moi
                  </Button>
                  <Button
                    variant="accent"
                    onClick={() => respond.mutate('interest')}
                    disabled={respond.isPending}
                  >
                    {respond.isPending ? 'Envoi…' : 'Ça m’intéresse'}
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  « Ça m’intéresse » crée une demande : Adweb-Systèmes est prévenu et vous pourrez
                  échanger dans le menu « Demandes ». Aucun engagement.
                </p>
              </div>
            )}
            {error && (
              <p role="alert" className="mt-2 text-xs text-red-600">
                {error}
              </p>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
